/**
 * Request schemas for the YouTube Optimizer API. Browser-safe (zod only), so
 * the web form validates with the same rules the API enforces.
 */

import { z } from "zod";
import { VIDEO_OPTIMIZER_LIMITS } from "./limits";
import { VIDEO_TYPES } from "./types";

export const videoPersonInputSchema = z.object({
	name: z.string().trim().min(1).max(120),
	role: z.string().trim().max(200).optional().default(""),
	organization: z.string().trim().max(200).optional().default(""),
});

export const videoLinksInputSchema = z.object({
	contacts: z.string().max(4000).optional().default(""),
	sources: z.string().max(4000).optional().default(""),
});

const termSchema = z.string().trim().min(1).max(120);

/** The browser-converted speech audio: the only source of a run's audio. */
export const audioFileSchema = z.object({
	fileName: z.string().trim().min(1).max(255),
	contentType: z.string().trim().min(1).max(100),
	sizeBytes: z
		.number()
		.int()
		.positive()
		.max(VIDEO_OPTIMIZER_LIMITS.maxAudioBytes),
	/** Read by the browser before converting; the server re-checks after upload. */
	durationSec: z
		.number()
		.positive()
		.max(VIDEO_OPTIMIZER_LIMITS.maxDurationSec)
		.optional(),
});

export const createVideoOptimizationSchema = z.object({
	propertyId: z.string().min(1),
	/** The speech audio the browser made from the video file (required). */
	file: audioFileSchema,
	/**
	 * Optional: the video's YouTube link. Read for the current title and
	 * description and to match a placement; never a source of audio.
	 */
	youtubeUrl: z.string().trim().url().max(500).optional(),
	/** "I have the rights to use this video." */
	rightsConfirmed: z.literal(true),
	name: z.string().trim().min(1).max(200).optional(),
	featuredPeople: z.array(videoPersonInputSchema).max(10).optional(),
	terms: z
		.array(termSchema)
		.max(VIDEO_OPTIMIZER_LIMITS.maxExtraTerms)
		.optional(),
	/** Omit to auto-detect. */
	videoType: z.enum(VIDEO_TYPES).optional(),
	outlet: z.string().trim().max(200).optional(),
	airDate: z.string().trim().max(60).optional(),
	links: videoLinksInputSchema.optional(),
});

export type CreateVideoOptimizationInput = z.infer<
	typeof createVideoOptimizationSchema
>;

export const videoUploadUrlSchema = audioFileSchema;

export const correctionInputSchema = z.object({
	id: z.string().max(64).optional(),
	heard: z.string().trim().min(1).max(200),
	fixed: z.string().trim().min(1).max(200),
	enabled: z.boolean().optional().default(true),
});

export const detectedPersonInputSchema = videoPersonInputSchema.extend({
	relation: z.enum(["featured", "subject"]).optional().default("featured"),
	/**
	 * The results page's "Save to the brand's people" toggle. Omitted means
	 * save; false keeps the person on this video only.
	 */
	save: z.boolean().optional(),
});

export const regenerateVideoOptimizationSchema = z.object({
	/** null clears an override (back to the detected type). */
	videoType: z.enum(VIDEO_TYPES).nullable().optional(),
	/** The confirmed people; replaces the run's people (and is saved per brand). */
	people: z.array(detectedPersonInputSchema).max(20).optional(),
	/** The full corrections list (heard → fixed, with on/off). */
	corrections: z.array(correctionInputSchema).max(100).optional(),
	terms: z
		.array(termSchema)
		.max(VIDEO_OPTIMIZER_LIMITS.maxExtraTerms)
		.optional(),
	outlet: z.string().trim().max(200).nullable().optional(),
	airDate: z.string().trim().max(60).nullable().optional(),
	links: videoLinksInputSchema.optional(),
});

export type RegenerateVideoOptimizationInput = z.infer<
	typeof regenerateVideoOptimizationSchema
>;

export const manualEditInputSchema = z.object({
	id: z.string().max(64).optional(),
	field: z.enum(["description", "title"]),
	titleIndex: z.number().int().min(0).max(2).optional(),
	before: z.string().min(1).max(5000),
	after: z.string().max(5000),
});

export const patchVideoOptimizationSchema = z.object({
	name: z.string().trim().min(1).max(200).optional(),
	selectedTitleIndex: z.number().int().min(0).max(2).optional(),
	/** The full list of hand edits (replaces the stored list). */
	manualEdits: z.array(manualEditInputSchema).max(100).optional(),
	reviewItemDecisions: z
		.array(
			z.object({
				id: z.string().min(1).max(64),
				status: z.enum(["open", "accepted", "dismissed"]),
			}),
		)
		.max(100)
		.optional(),
});

export type PatchVideoOptimizationInput = z.infer<
	typeof patchVideoOptimizationSchema
>;

export const checkYouTubeSchema = z.object({
	/** For a run made without a link: the video's YouTube link, set once. */
	youtubeUrl: z.string().trim().url().max(500).optional(),
});

export const putVideoOptimizerPeopleSchema = z.object({
	people: z
		.array(videoPersonInputSchema)
		.max(VIDEO_OPTIMIZER_LIMITS.maxSavedPeople),
});

export const putVideoOptimizerTermsSchema = z.object({
	terms: z.array(termSchema).max(VIDEO_OPTIMIZER_LIMITS.maxSavedTerms),
});
